import { useState, useEffect, useMemo, useRef } from "react";
import L from "leaflet";
import { Polyline, Marker, Popup, Tooltip, useMap } from "react-leaflet"; // TAMBAKAN useMap
import { DashboardLayout } from "../../../../components/layouts/DashboardLayout";
import { Header } from "../../../../components/Header";
import { MapView } from "../../../../utilities/leftjs/MapView";
import { BaseUrl } from "../../../../utilities/BaseUrl";

// ==================== IMPORT IKON ====================
import IcPop from "../../../../assets/icons/ic_pop.png";
import IcOdc from "../../../../assets/icons/ic_odc.png";
import IcOdp from "../../../../assets/icons/ic_odp.png";
import IcUser from "../../../../assets/icons/ic_location_dot.png";

import {
  FaLayerGroup,
  FaSitemap,
  FaChevronRight,
} from "react-icons/fa6";

// ==================== KONFIGURASI ====================
const LINE_COLORS = {
  pop_to_odc: "#06b6d4",
  odc_to_odp: "#f59e0b",
  odp_to_pelanggan: "#10b981",
};

const ICON_SIZE = [40, 40];
const ICON_ANCHOR = [ICON_SIZE[0] / 2, ICON_SIZE[1]];

const ICONS = {
  pop: L.icon({ iconUrl: IcPop, iconSize: ICON_SIZE, iconAnchor: ICON_ANCHOR }),
  odc: L.icon({ iconUrl: IcOdc, iconSize: ICON_SIZE, iconAnchor: ICON_ANCHOR }),
  odp: L.icon({ iconUrl: IcOdp, iconSize: ICON_SIZE, iconAnchor: ICON_ANCHOR }),
  pelanggan: L.icon({ iconUrl: IcUser, iconSize: ICON_SIZE, iconAnchor: ICON_ANCHOR }),
};

const getLineColor = (fromType, toType) => {
  if (fromType === "pop" && toType === "odc") return LINE_COLORS.pop_to_odc;
  if (fromType === "odc" && toType === "odp") return LINE_COLORS.odc_to_odp;
  if (fromType === "odp" && toType === "pelanggan") return LINE_COLORS.odp_to_pelanggan;
  return "#64748b";
};

// ==================== KOMPONEN LEGENDA ====================
const LegendLine = ({ color, label }) => (
  <div className="flex items-center gap-2 pl-1">
    <div className="w-6 h-[3px] rounded-full" style={{ backgroundColor: color }} />
    <span className="text-[11px] text-gray-400">{label}</span>
  </div>
);

const LegendItem = ({ icon, label }) => (
  <div className="flex items-center gap-2">
    <img src={icon} alt={label} className="w-5 h-5 object-contain" />
    <span className="text-xs text-gray-200">{label}</span>
  </div>
);

const MapLegend = ({ markers }) => {
  const countByType = markers.reduce((acc, m) => {
    acc[m.type] = (acc[m.type] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="absolute left-3 bottom-3 z-[1000] bg-gray-900/90 backdrop-blur-sm rounded-lg p-3 border border-gray-700/50 shadow-xl">
      <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Legenda</p>
      <div className="space-y-1.5">
        <LegendItem icon={IcPop} label="POP" count={countByType.pop || 0} />
        <LegendLine color={LINE_COLORS.pop_to_odc} label="POP ➔ ODC" />
        <LegendItem icon={IcOdc} label="ODC" count={countByType.odc || 0} />
        <LegendLine color={LINE_COLORS.odc_to_odp} label="ODC ➔ ODP" />
        <LegendItem icon={IcOdp} label="ODP" count={countByType.odp || 0} />
        <LegendLine color={LINE_COLORS.odp_to_pelanggan} label="ODP ➔ Pelanggan" />
        <LegendItem icon={IcUser} label="Pelanggan" count={countByType.pelanggan || 0} />
      </div>
    </div>
  );
};

// ==================== KOMPONEN ANIMASI GARIS ====================
const FlowLine = ({ positions, color, weight = 4, opacity = 0.9 }) => {
  const lineRef = useRef(null);

  useEffect(() => {
    if (lineRef.current) {
      const pathElement = lineRef.current._path; 
      if (pathElement) {
        pathElement.classList.add('flow-animation');
        pathElement.style.strokeDasharray = "8, 16";
      }
    }
  }, [lineRef.current]);

  return (
    <Polyline 
      ref={lineRef}
      positions={positions} 
      pathOptions={{ color, weight, opacity }}
    />
  );
};

// ==================== KOMPONEN PENGUBAH VIEW (FLY TO) ====================
// Komponen wajib untuk menggerakkan peta secara dinamis tanpa reload
const ChangeView = ({ center, zoom }) => {
  const map = useMap();
  
  useEffect(() => {
    if (center && center[0] && center[1]) {
      map.flyTo(center, zoom || 16, {
        duration: 1.5 // Durasi animasi fly 1.5 detik
      });
    }
  }, [center, zoom, map]);

  return null;
};

// ==================== KOMPONEN UTAMA ====================
export const Mapping = () => {
  const [showSidebar, setShowSidebar] = useState(true);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  
  const [mapViewLevel, setMapViewLevel] = useState("all");
  const [selectedNode, setSelectedNode] = useState(null);

  // ==================== LOGIKA DINAMIS CENTER ====================
  const mapCenter = useMemo(() => {
    // 1. Jika sedang drill down (klik node tertentu), fokuskan ke node tersebut
    if (selectedNode && selectedNode.lat && selectedNode.lng) {
      return [selectedNode.lat, selectedNode.lng];
    }
    
    // 2. Jika mode "All", fokuskan ke titik data pertama yang ditemukan
    if (data?.markers?.length > 0) {
      const firstMarker = data.markers[0];
      if (firstMarker.lat && firstMarker.lng) {
        return [firstMarker.lat, firstMarker.lng];
      }
    }
    
    // 3. Fallback kosong (tidak akan pernah dipakai jika data ada)
    return null;
  }, [data, selectedNode]);

  // ==================== FETCH DATA ====================
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("token-radiustrend");
        let url = `${BaseUrl}/api/v1/mapping?level=${mapViewLevel}`;
        
        if (mapViewLevel !== "all" && selectedNode?.id) {
          url += `&id=${selectedNode.id}`;
        }

        const response = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const result = await response.json();
        
        if (result.success) {
          setData(result.data);
        }
      } catch (error) {
        console.error("Error fetching map data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [mapViewLevel, selectedNode]);

  // ==================== LOGIKA KLIK MARKER ====================
  const handleDrillDown = (marker) => {
    setSelectedNode(marker);
    setMapViewLevel(marker.type); 
  };

  const handleResetView = () => {
    setMapViewLevel("all");
    setSelectedNode(null);
  };

  // ==================== GENERATE POLYLINES ====================
  const polylines = useMemo(() => {
    if (!data?.lines || !data?.markers) return [];

    return data.lines
      .map((line) => {
        const fromPoint = data.markers.find((m) => m.id === line.from);
        const toPoint = data.markers.find((m) => m.id === line.to);

        if (!fromPoint || !toPoint) return null;

        return {
          id: `line-${line.from}-${line.to}`,
          positions: [
            [fromPoint.lat, fromPoint.lng],
            [toPoint.lat, toPoint.lng],
          ],
          color: getLineColor(fromPoint.type, toPoint.type),
        };
      })
      .filter(Boolean); 
  }, [data]);

  // ==================== LOADING STATE ====================
  if (loading) {
    return (
      <DashboardLayout showSidebar={true} setShowSidebar={setShowSidebar}>
        <Header showToggleSidebar={false}>
          <h2 className="text-base md:text-xl font-bold text-gray-100">Mapping Infrastruktur</h2>
        </Header>
        <main className="p-4 lg:p-6">
          <div className="w-full aspect-video rounded-xl bg-gray-800 animate-pulse flex items-center justify-center">
            <span className="text-gray-500">Memuat peta infrastruktur...</span>
          </div>
        </main>
      </DashboardLayout>
    );
  }

  // ==================== RENDER ====================
  return (
    <DashboardLayout showSidebar={showSidebar} setShowSidebar={setShowSidebar}>
      <Header toggleSidebar={() => setShowSidebar(!showSidebar)}>
        <div className="flex items-center gap-4 flex-wrap">
          <h2 className="text-base md:text-xl font-bold text-gray-100">Mapping Infrastruktur</h2>
          
          <div className="flex items-center gap-2 text-xs">
            <button 
              onClick={handleResetView}
              className={`font-bold transition-colors ${mapViewLevel === 'all' ? 'text-cyan-400' : 'text-slate-500 hover:text-slate-300'}`}
            >
              General
            </button>
            
            {selectedNode && (
              <>
                <FaChevronRight size={8} className="text-slate-600" />
                <span className="text-cyan-400 font-bold">{selectedNode.name}</span>
              </>
            )}
          </div>

          <span className="text-xs bg-gray-800 text-gray-400 px-2.5 py-1 rounded-full border border-gray-700">
            {data?.markers?.length || 0} titik · {polylines.length} jalur
          </span>
        </div>
      </Header>

      <main className="p-4 lg:p-6 w-full">
        <div className="w-full h-[calc(100vh-100px)] relative z-0 rounded-xl overflow-clip">
          <MapView center={mapCenter || [-6.917464, 107.619123]} zoom={16}>
            
            {/* PANGGIL KOMPONEN CHANGE VIEW DI SINI */}
            {mapCenter && <ChangeView center={mapCenter} zoom={16} />}

            <style>{`
              .flow-animation {
                animation: moveDash 1.5s linear infinite;
                filter: drop-shadow(0 0 4px currentColor); 
              }
              @keyframes moveDash {
                to {
                  stroke-dashoffset: -25; 
                }
              }
            `}</style>

            {polylines.map((pl) => (
              <FlowLine 
                key={pl.id}
                positions={pl.positions}
                color={pl.color}
                weight={4}
                opacity={0.9}
              />
            ))}

            {data?.markers?.map((m) => {
              const icon = ICONS[m.type] || ICONS.pelanggan;
              const isOrphan = m.parent_id === null && m.type === "pelanggan"; 
              
              return (
                <Marker key={`marker-${m.id}`} position={[m.lat, m.lng]} icon={icon}>
                  <Tooltip direction="top" offset={[0, -40]}>
                    <span className="font-bold text-xs">{m.name} {isOrphan ? "(Orphan)" : ""}</span>
                  </Tooltip>
                  <Popup>
                    <div className="text-center p-1 min-w-[150px]">
                      <p className="font-extrabold text-sm">{m.name}</p>
                      <p className="text-[10px] text-gray-500 uppercase font-bold mt-1 bg-slate-100 inline-block px-2 py-0.5 rounded">
                        {m.type}
                      </p>
                      
                      {isOrphan && (
                        <p className="text-[10px] text-amber-600 mt-2 font-semibold">
                          ⚠️ Tidak terhubung ke ODP
                        </p>
                      )}

                      {m.type !== "pelanggan" && (
                        <div className="mt-3 pt-2 border-t border-gray-200">
                          <button
                            onClick={() => handleDrillDown(m)}
                            className="w-full bg-cyan-500 hover:bg-cyan-600 text-white text-[10px] font-bold py-1.5 px-3 rounded-md transition-colors shadow-sm"
                          >
                            Lihat Cabang {m.type.toUpperCase()} ➔
                          </button>
                        </div>
                      )}
                    </div>
                  </Popup>
                </Marker>
              );
            })}

            <div className="absolute top-4 left-4 z-[1000] flex flex-col gap-2">
              <div className="bg-slate-900/90 backdrop-blur text-white text-xs p-3 rounded-lg border border-slate-700 shadow-xl min-w-[220px]">
                <p className="font-bold mb-2 text-slate-400 flex items-center gap-2">
                  <FaSitemap /> TAMPILAN PETA:
                </p>
                <div className="flex flex-col gap-2">
                  
                  <button 
                    onClick={handleResetView}
                    className={`px-3 py-2 rounded text-left transition-all flex items-center gap-2 ${
                      mapViewLevel === 'all' 
                        ? 'bg-indigo-600 text-white font-bold shadow-lg shadow-indigo-500/30' 
                        : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                    }`}
                  >
                    <FaLayerGroup size={12} /> General (Semua)
                  </button>

                  {mapViewLevel !== 'all' && selectedNode && (
                    <>
                      <div className="h-px bg-slate-700 my-1"></div>
                      <button 
                        onClick={() => handleDrillDown(selectedNode)}
                        className={`px-3 py-2 rounded text-left transition-all capitalize ${
                          'bg-slate-800 text-cyan-400 font-bold border border-cyan-500/30'
                        }`}
                      >
                        🔍 Focus: {selectedNode.name}
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>

            <MapLegend markers={data?.markers || []} />
          </MapView>
        </div>
      </main>
    </DashboardLayout>
  );
};